<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Day Book
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2 align-center">
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"                      
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"                      
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadDayBook" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="dayBookData && dayBookData.length > 0" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!dayBookData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!dayBookData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Day Book Report -->
                <v-card v-if="dayBookData && !loading && dayBookData.length > 0" class="mt-2">
                  <v-card-title class=" text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">Day Book</div>
                      <div class="text-h6 font-weight-medium">
                        {{ formattedDate(filters.from_date) }} to {{ formattedDate(filters.to_date) }}
                      </div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Day Book Report -->
                    <div class="daybook-report">
                   

                      <!-- Day Book Table -->
                      <div class="daybook-table">
                        <!-- Table Header -->
                        <div class="daybook-table-header">
                          <div class="header-serial">S/L</div>
                          <div class="header-date">Date</div>
                          <div class="header-particulars">Particulars</div>
                          <div class="header-voucher-type">Voucher Type</div>
                          <div class="header-voucher-no">Voucher No</div>
                          <div class="header-debit">Debit Amount</div>
                          <div class="header-credit">Credit Amount</div>
                        </div>

                        <!-- Table Body -->
                        <div class="daybook-table-body">
                          <template v-for="(row, index) in flattenedRows" :key="index">
                            <div class="daybook-table-row">
                              <div class="cell-serial">{{ index + 1 }}</div>
                              <div class="cell-date">{{ formattedDate(row.date) }}</div>
                              <div class="cell-particulars" style="white-space: pre-line;">{{ row.particulars }}</div>
                              <div class="cell-voucher-type">{{ row.voucher_type }}</div>
                              <div class="cell-voucher-no">{{ row.voucher_no }}</div>
                              <div class="cell-debit">{{ row.debit_amount }}</div>
                              <div class="cell-credit">{{ row.credit_amount }}</div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data Message -->
                <v-card v-if="!loading && dayBookData && dayBookData.length === 0" class="mt-2">
                  <v-card-text class="text-center py-10">
                    <v-icon size="64" color="grey">mdi-database-search</v-icon>
                    <div class="text-h6 mt-4 text-grey">No data found for the selected criteria</div>
                  </v-card-text>
                </v-card>

                <!-- Loading -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-10">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="text-h6 mt-4">Loading...</div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import Axios from 'axios'
import { getDayBookReport } from '@/store/api'
import formattedDate from '@/plugins/dateFormatter';

export default {
  name: 'DayBook',
  data() {
    return {
      panel: [0],
      loading: false,
      dayBookData: [],
      filters: {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      }
    }
  },
  computed: {
    flattenedRows() {
      if (!this.dayBookData || this.dayBookData.length === 0) return []
      
      const rows = []
      
      this.dayBookData.forEach(voucher => {
        const particulars = []
        
        // Process debit transactions
        if (voucher.debit_transactions && Array.isArray(voucher.debit_transactions)) {
          voucher.debit_transactions.forEach((transaction) => {
            particulars.push(`${transaction.account_head} ${this.formatCurrency(transaction.amount)} Dr.${transaction.remarks ? ' - ' + transaction.remarks : ''}`)
          })
        }
        
        // Process credit transactions
        if (voucher.credit_transactions && Array.isArray(voucher.credit_transactions)) {
          voucher.credit_transactions.forEach((transaction) => {
            particulars.push(`To, ${transaction.account_head} ${this.formatCurrency(transaction.amount)} Cr.${transaction.remarks ? ' - ' + transaction.remarks : ''}`)
          })
        }
        
        // Create single row for this voucher
        rows.push({
          date: voucher.voucher_date,
          particulars: particulars.join('\n'),
          voucher_type: voucher.voucher_type?.name || 'N/A',
          voucher_no: voucher.code,
          debit_amount: this.formatCurrency(voucher.debit_amount),
          credit_amount: this.formatCurrency(voucher.credit_amount)
        })
      })
      
      return rows
    },
    totalDebit() {
      return this.dayBookData.reduce((sum, voucher) => {
        return sum + (voucher.debit_amount || 0)
      }, 0)
    },
    totalCredit() {
      return this.dayBookData.reduce((sum, voucher) => {
        return sum + (voucher.credit_amount || 0)
      }, 0)
    }
  },
  mounted() {
    // Load data on mount
    this.loadDayBook()
  },
  methods: {
    formattedDate,
    async loadDayBook() {
      if (!this.filters.from_date || !this.filters.to_date) {
        this.$store.commit('SET_SNACKBAR', {
          snackbar: true,
          text: 'Please select both from and to dates',
          color: 'error'
        })
        return
      }

      this.loading = true
      try {
        const params = new URLSearchParams()
        params.append('from_date', formattedDate(this.filters.from_date))
        params.append('to_date', formattedDate(this.filters.to_date))

        const response = await Axios.get(`${getDayBookReport}?${params.toString()}`)
        
        if (response.data.success) {
          this.dayBookData = response.data.data || []
        } else {
          this.$store.commit('SET_SNACKBAR', {
            snackbar: true,
            text: response.data.message || 'Failed to load Day Book',
            color: 'error'
          })
        }
      } catch (error) {
        console.error('Error loading Day Book:', error)
        this.$store.commit('SET_SNACKBAR', {
          snackbar: true,
          text: 'Error loading Day Book data',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    
    resetFilters() {
      this.filters = {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      }
      this.dayBookData = []
    },
    
    formatCurrency(amount) {
      if (amount === 0 || amount === '-') return '-'
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    },
    
    exportToExcel() {
      if (!this.dayBookData || this.dayBookData.length === 0) return

      const data = []
      
      // Header
      data.push(['Day Book'])
      data.push([`From: ${this.formattedDate(this.filters.from_date)} To: ${this.formattedDate(this.filters.to_date)}`])
      data.push([])
      data.push(['S/L', 'Date', 'Particulars', 'Voucher Type', 'Voucher No', 'Debit Amount', 'Credit Amount'])

      // Rows
      this.flattenedRows.forEach((row, index) => {
        data.push([
          index + 1,
          this.formattedDate(row.date),
          row.particulars.replace(/\n/g, '\n'), // Preserve line breaks
          row.voucher_type,
          row.voucher_no,
          row.debit_amount,
          row.credit_amount
        ])
      })

      // Totals
      data.push([])
      data.push(['', '', 'Total', '', '', this.formatCurrency(this.totalDebit), this.formatCurrency(this.totalCredit)])

      this.$exportTableToExcel(data, 'day_book_' + this.$fileDate())
    },
    
    exportToPDF() {
      if (!this.dayBookData || this.dayBookData.length === 0) return

      const columns = ['S/L', 'Date', 'Particulars', 'Voucher Type', 'Voucher No', 'Debit Amount', 'Credit Amount']
      const rows = []

      // Rows
      this.flattenedRows.forEach((row, index) => {
        rows.push([
          index + 1,
          this.formattedDate(row.date),
          row.particulars.replace(/\n/g, '\n'), // Preserve line breaks
          row.voucher_type,
          row.voucher_no,
          row.debit_amount,
          row.credit_amount
        ])
      })

      // Totals
      rows.push([
        '',
        '',
        'Total',
        '',
        '',
        this.formatCurrency(this.totalDebit),
        this.formatCurrency(this.totalCredit)
      ])

      this.$exportTableToPDF(
        columns,
        rows,
        `Day Book - ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`,
        'day_book_' + this.$fileDate(),
        'landscape',
        {
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fontSize: 8, fillColor: [66, 139, 202], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' },  // S/L
            1: { cellWidth: 25, halign: 'center' },  // Date
            2: { cellWidth: 80, halign: 'left' },    // Particulars
            3: { cellWidth: 35, halign: 'center' },  // Voucher Type
            4: { cellWidth: 30, halign: 'center' },  // Voucher No
            5: { cellWidth: 30, halign: 'right' },   // Debit Amount
            6: { cellWidth: 30, halign: 'right' }    // Credit Amount
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: bold;
  font-size: 1.1rem;
}

/* Day Book Report Style */
.daybook-report {
  margin-bottom: 20px;
}

.daybook-header {
  padding: 4px 8px;
  border-bottom: 1px solid #000;
  font-weight: bold;
  text-align: center;
  font-size: 12px;
}

.daybook-table {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
  border: 1px solid #000;
  overflow: hidden;
  table-layout: fixed;
}

/* Header Styles */
.daybook-table-header {
  display: flex;
  align-items: stretch;
  font-weight: bold;
  border-bottom: 1px solid #000;
  background-color: #f5f5f5;
}

.header-serial { width: 50px; padding: 2px 4px; font-size: 11px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.header-date { width: 100px; padding: 2px 4px; font-size: 11px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.header-particulars { flex: 1; padding: 2px 4px; font-size: 11px; text-align: left; border-right: 1px solid #000; display: flex; align-items: center; }
.header-voucher-type { width: 120px; padding: 2px 4px; font-size: 11px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.header-voucher-no { width: 120px; padding: 2px 4px; font-size: 11px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.header-debit { width: 130px; padding: 2px 4px; font-size: 11px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.header-credit { width: 130px; padding: 2px 4px; font-size: 11px; text-align: center; }

/* Row Styles */
.daybook-table-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #000;
  min-height: 30px;
}

.daybook-table-row:last-child {
  border-bottom: none;
}

.daybook-table-row.totals-row {
  background-color: #f0f0f0;
  font-weight: bold;
  border-top: 2px solid #000;
}

/* Cell Styles */
.cell-serial { width: 50px; padding: 2px 4px; font-size: 10px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.cell-date { width: 100px; padding: 2px 4px; font-size: 10px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.cell-particulars { flex: 1; padding: 2px 4px; font-size: 10px; text-align: left; border-right: 1px solid #000; display: flex; align-items: center; }
.cell-voucher-type { width: 120px; padding: 2px 4px; font-size: 10px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.cell-voucher-no { width: 120px; padding: 2px 4px; font-size: 10px; text-align: center; border-right: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.cell-debit { width: 130px; padding: 2px 4px; font-size: 10px; text-align: right; border-right: 1px solid #000; display: flex; align-items: center; justify-content: flex-end; }
.cell-credit { width: 130px; padding: 2px 4px; font-size: 10px; text-align: right; display: flex; align-items: center; justify-content: flex-end; }

.w-100 {
  width: 100%;
}
</style>

